import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';
import { POLL_INTERVALS, smartQueryDefaults, smartRefetchInterval } from '../utils/queryPolling';

// Query keys
export const messageKeys = {
  all: ['messages'],
  conversations: () => [...messageKeys.all, 'conversations'],
  conversation: (id) => [...messageKeys.conversations(), id],
  unreadCount: () => [...messageKeys.all, 'unread-count'],
};

// API functions
export const messageApi = {
  getConversations: async () => {
    const response = await api.get('/conversations');
    return response.data;
  },

  getMessages: async (conversationId) => {
    const response = await api.get(`/conversations/${conversationId}/messages`);
    return response.data;
  },

  sendMessage: async ({ conversationId, message }) => {
    const response = await api.post(`/conversations/${conversationId}/messages`, { message });
    return response.data;
  },

  markAsRead: async (messageId) => {
    const response = await api.put(`/messages/${messageId}/read`);
    return response.data;
  },

  deleteMessage: async (messageId) => {
    const response = await api.delete(`/messages/${messageId}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/messages/unread-count');
    return response.data?.data?.unread_count ?? 0;
  },
};

// React Query hooks
export const useConversations = () => {
  return useQuery({
    queryKey: messageKeys.conversations(),
    queryFn: messageApi.getConversations,
    staleTime: 30 * 1000,
    ...smartQueryDefaults,
  });
};

export const useMessages = (conversationId) => {
  return useQuery({
    queryKey: messageKeys.conversation(conversationId),
    queryFn: () => messageApi.getMessages(conversationId),
    enabled: !!conversationId,
    refetchInterval: smartRefetchInterval(POLL_INTERVALS.CHAT_MESSAGES),
    ...smartQueryDefaults,
  });
};

export const useUnreadMessagesCount = () => {
  return useQuery({
    queryKey: messageKeys.unreadCount(),
    queryFn: messageApi.getUnreadCount,
    staleTime: 30 * 1000,
    refetchInterval: smartRefetchInterval(POLL_INTERVALS.BADGE),
    ...smartQueryDefaults,
  });
};

export const useSendMessage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, message }) => messageApi.sendMessage({ conversationId, message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
    },
    ...options,
  });
};

export const useMarkMessageAsRead = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messageApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount() });
    },
    ...options,
  });
};

export const useDeleteMessage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: messageApi.deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.conversations() });
    },
    ...options,
  });
};
